import { Avatar, Stack, Typography, Tooltip } from '@ahamove/web-components';
import { tooltipClasses } from '@ahamove/web-components/lib/Tooltip';
import { useTranslation } from 'next-i18next';
import { homeConfig } from '@/modules/home/homeConfig';
import useIsMobile from '@/shared-hooks/useIsMobile';
import type { AvailableEV } from '../NewBookingProvider';

type Props = {
  data: AvailableEV;
};

export default function EVtemDesc({ data }: Props) {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const isMobile = useIsMobile();

  return (
    <Stack
      data-test-id="evItemDescContainer"
      direction="row"
      justifyContent={isMobile ? 'space-between' : 'flex-start'}
      flexWrap="wrap"
      gap={isMobile ? '4px' : '40px'}
      spacing={isMobile ? 0.5 : 0}
    >
      {data?.vehicle_model?.parameters?.number_of_seats > 0 && (
        <Tooltip
          enterTouchDelay={0}
          arrow
          title={t('booking:ev.tooltip.number_of_seats', {
            number: data?.vehicle_model?.parameters?.number_of_seats,
          })}
          PopperProps={{
            sx: {
              [`.${tooltipClasses.tooltip}`]: {
                bgcolor: '#21272A',
              },
              [`.${tooltipClasses.arrow}`]: {
                color: '#21272A',
              },
            },
          }}
        >
          <Typography
            whiteSpace="nowrap"
            variant={isMobile ? 'caption' : 'body1'}
            color="grey.500"
            fontWeight={500}
            sx={{
              width: isMobile ? 'calc(50% - 10px)' : 'fit-content',
              display: 'flex',
              alignItems: 'center',
              marginLeft: '0 !important',
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                marginRight: '8px',
                width: 16,
                height: 16,
              }}
              src="/static/images/CarSeat.png"
            />
            {t('booking:ev.number_of_seats', {
              number: data?.vehicle_model?.parameters?.number_of_seats,
            })}
          </Typography>
        </Tooltip>
      )}

      {data?.vehicle_model?.parameters?.time_to_full_charging_in_hour > 0 && (
        <Tooltip
          enterTouchDelay={0}
          arrow
          title={t('booking:ev.tooltip.time_to_full_charging_in_hour', {
            number:
              data?.vehicle_model?.parameters?.time_to_full_charging_in_hour,
          })}
          PopperProps={{
            sx: {
              [`.${tooltipClasses.tooltip}`]: {
                bgcolor: '#21272A',
              },
              [`.${tooltipClasses.arrow}`]: {
                color: '#21272A',
              },
            },
          }}
        >
          <Typography
            whiteSpace="nowrap"
            variant={isMobile ? 'caption' : 'body1'}
            color="grey.500"
            fontWeight={500}
            sx={{
              width: isMobile ? 'calc(50% - 10px)' : 'fit-content',
              display: 'flex',
              alignItems: 'center',
              marginLeft: '0 !important',
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                marginRight: '8px',
                width: 16,
                height: 16,
              }}
              src="/static/images/BatteryChargeFill.png"
            />
            {t('booking:ev.time_to_full_charging_in_hour', {
              number:
                data?.vehicle_model?.parameters?.time_to_full_charging_in_hour,
            })}
          </Typography>
        </Tooltip>
      )}

      {data?.vehicle_model?.parameters?.max_speed_in_km_per_hour > 0 && (
        <Tooltip
          enterTouchDelay={0}
          arrow
          title={t('booking:ev.tooltip.max_speed_in_km_per_hour', {
            number: data?.vehicle_model?.parameters?.max_speed_in_km_per_hour,
          })}
          PopperProps={{
            sx: {
              [`.${tooltipClasses.tooltip}`]: {
                bgcolor: '#21272A',
              },
              [`.${tooltipClasses.arrow}`]: {
                color: '#21272A',
              },
            },
          }}
        >
          <Typography
            whiteSpace="nowrap"
            variant={isMobile ? 'caption' : 'body1'}
            color="grey.500"
            fontWeight={500}
            sx={{
              width: isMobile ? 'calc(50% - 10px)' : 'fit-content',
              display: 'flex',
              alignItems: 'center',
              marginLeft: '0 !important',
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                marginRight: '8px',
                width: 16,
                height: 16,
              }}
              src="/static/images/Speedometer.png"
            />
            {t('booking:ev.max_speed_in_km_per_hour', {
              number: data?.vehicle_model?.parameters?.max_speed_in_km_per_hour,
            })}
          </Typography>
        </Tooltip>
      )}

      {data?.vehicle_model?.parameters?.max_distance_in_km > 0 && (
        <Tooltip
          enterTouchDelay={0}
          arrow
          title={t('booking:ev.tooltip.max_distance_in_km', {
            number: data?.vehicle_model?.parameters?.max_distance_in_km,
          })}
          PopperProps={{
            sx: {
              [`.${tooltipClasses.tooltip}`]: {
                bgcolor: '#21272A',
              },
              [`.${tooltipClasses.arrow}`]: {
                color: '#21272A',
              },
            },
          }}
        >
          <Typography
            whiteSpace="nowrap"
            variant={isMobile ? 'caption' : 'body1'}
            color="grey.500"
            fontWeight={500}
            sx={{
              width: isMobile ? 'calc(50% - 10px)' : 'fit-content',
              display: 'flex',
              alignItems: 'center',
              marginLeft: '0 !important',
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                marginRight: '8px',
                width: 16,
                height: 16,
              }}
              src="/static/images/RoadBattery.png"
            />
            {t('booking:ev.max_distance_in_km', {
              number: data?.vehicle_model?.parameters?.max_distance_in_km,
            })}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
}
